import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, UserCog } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState, Pagination, TBody, TD, TH, THead, TR, Table } from '@/components/ui/Table';
import { SectionSpinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/Toast';
import { usersApi, type UserRow } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';
import { formatDateTime } from '@/lib/dateUtils';

export function UsersPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<UserRow | null>(null);

  const [form, setForm] = useState({
    username: '',
    password: '',
    fullName: '',
    role: 'FINANCE' as 'ADMIN' | 'FINANCE',
  });
  const [newPassword, setNewPassword] = useState('');

  const params = { page, pageSize: 25 };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.users.list(params),
    queryFn: () => usersApi.list(params),
    placeholderData: (previous) => previous,
  });

  const createUser = useMutation({
    mutationFn: () => usersApi.create(form),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success(t('user.created'));
      setCreateOpen(false);
      setForm({ username: '', password: '', fullName: '', role: 'FINANCE' });
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : t('errors.unknown')),
  });

  const toggleActive = useMutation({
    mutationFn: (user: UserRow) => usersApi.update(user.id, { isActive: !user.isActive }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      toast.success(t('user.updated'));
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : t('errors.unknown')),
  });

  const resetPassword = useMutation({
    mutationFn: () => usersApi.resetPassword(resetTarget!.id, newPassword),
    onSuccess: () => {
      toast.success(t('user.passwordReset'));
      setResetTarget(null);
      setNewPassword('');
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : t('errors.unknown')),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('user.title')}
        description={t('user.agentLoginNote')}
        actions={
          <Button onClick={() => setCreateOpen(true)} leftIcon={<Plus className="h-4 w-4" />}>
            {t('user.new')}
          </Button>
        }
      />

      <Card>
        {isLoading ? (
          <SectionSpinner />
        ) : !data || data.rows.length === 0 ? (
          <EmptyState icon={<UserCog className="h-6 w-6" />} title={t('common.noResults')} />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>{t('user.username')}</TH>
                  <TH>{t('user.fullName')}</TH>
                  <TH>{t('user.role')}</TH>
                  <TH>{t('user.lastLogin')}</TH>
                  <TH align="center">{t('common.status')}</TH>
                  <TH align="right">{t('common.actions')}</TH>
                </TR>
              </THead>

              <TBody>
                {data.rows.map((user) => (
                  <TR key={user.id}>
                    <TD className="font-medium text-slate-900">{user.username}</TD>
                    <TD>{user.fullName}</TD>
                    <TD>
                      <Badge tone={user.role === 'ADMIN' ? 'info' : 'neutral'}>
                        {t(`user.roles.${user.role}` as never)}
                      </Badge>
                    </TD>
                    <TD className="text-xs text-slate-500">{formatDateTime(user.lastLoginAt)}</TD>
                    <TD align="center">
                      <div className="flex flex-col items-center gap-1">
                        <Badge tone={user.isActive ? 'success' : 'neutral'}>
                          {user.isActive ? t('common.active') : t('common.inactive')}
                        </Badge>
                        {user.mustChangePassword && (
                          <span className="text-[10px] text-amber-600">
                            {t('auth.changePassword')}
                          </span>
                        )}
                      </div>
                    </TD>
                    <TD align="right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setResetTarget(user)}
                          title={t('user.resetPassword')}
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        {/* Agent logins are managed on the agent record. */}
                        {user.role !== 'AGENT' && (
                          <Button variant="ghost" size="sm" onClick={() => toggleActive.mutate(user)}>
                            {user.isActive ? t('common.inactive') : t('common.active')}
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>

            <Pagination meta={data.meta} onPageChange={setPage} />
          </>
        )}
      </Card>

      <Modal
        isOpen={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        title={t('user.new')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => createUser.mutate()}
              isLoading={createUser.isPending}
              disabled={
                form.username.trim().length < 3 ||
                form.password.length < 8 ||
                !form.fullName.trim()
              }
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label={t('user.fullName')}
            value={form.fullName}
            onChange={(event) => setForm({ ...form, fullName: event.target.value })}
            required
            autoFocus
          />
          <Input
            label={t('user.username')}
            value={form.username}
            onChange={(event) => setForm({ ...form, username: event.target.value })}
            autoComplete="off"
            required
          />
          <Input
            label={t('auth.password')}
            type="password"
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
            hint={t('auth.passwordRules')}
            autoComplete="new-password"
            required
          />
          <Select
            label={t('user.role')}
            value={form.role}
            onChange={(event) =>
              setForm({ ...form, role: event.target.value as 'ADMIN' | 'FINANCE' })
            }
            options={[
              { value: 'FINANCE', label: t('user.roles.FINANCE') },
              { value: 'ADMIN', label: t('user.roles.ADMIN') },
            ]}
            required
          />
        </div>
      </Modal>

      <Modal
        isOpen={resetTarget !== null}
        onClose={() => setResetTarget(null)}
        title={t('user.resetPassword')}
        description={resetTarget?.username}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setResetTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => resetPassword.mutate()}
              isLoading={resetPassword.isPending}
              disabled={newPassword.length < 8}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <Input
          label={t('auth.newPassword')}
          type="password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          hint={t('auth.passwordRules')}
          autoComplete="new-password"
          required
          autoFocus
        />
      </Modal>
    </div>
  );
}
